import React, { createContext, useContext, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import type { RangeKey, RangeParams } from '../../services/adminApi';

/**
 * The window types live in `services/adminApi`, which is what actually sends
 * them, and are re-exported here because the console's UI has always imported
 * them from this file. `services/` may not import from `pages/`, so the
 * dependency has to run this way round.
 */
export type { RangeKey, RangeParams } from '../../services/adminApi';

/** The presets, in the ascending order the header offers them. */
export const RANGE_KEYS: RangeKey[] = ['7d', '30d', '90d'];

/**
 * The widest window the API will answer, from `MAX_RANGE_SPAN` in
 * `admin/utils.py`. The backend REFUSES an over-wide window with a 422 rather
 * than narrowing it, deliberately — a page that quietly answers a narrower
 * question than the one in its own header is the failure the console cannot
 * afford. So the picker below has to refuse the same windows the server does,
 * or the UI hands itself an error out of its own control.
 */
export const MAX_RANGE_SPAN_DAYS = 180;
const MAX_RANGE_SPAN_MS = MAX_RANGE_SPAN_DAYS * 24 * 60 * 60 * 1000;

/**
 * The instants a picked pair of calendar days is sent as.
 *
 * `until` is the END of the selected day, not its midnight. Midnight would drop
 * the whole of the end date from the answer while the header kept claiming it —
 * the same silent-narrowing sin the backend refuses to commit.
 *
 * That choice is what fixes the span cap's boundary. The server measures
 * `until - since` on instants, so the widest pair it will answer is 180
 * CALENDAR DAYS inclusive: `startOf(day 1) .. endOf(day 180)` is 179d 23:59:59,
 * comfortably inside the cap, while a 181st day pushes it to 180d 23:59:59 and
 * is refused. Measuring the real serialised instants rather than a calendar-day
 * count also keeps this correct across a DST boundary, where a 180-day window
 * is really 180d ± 1h.
 */
export const serializeCustomRange = (
  since: Dayjs,
  until: Dayjs,
): { since: string; until: string } => ({
  since: since.startOf('day').toISOString(),
  until: until.endOf('day').toISOString(),
});

/** True when the window these two days would be sent as exceeds the server's cap. */
export const isRangeSpanTooWide = (a: Dayjs, b: Dayjs): boolean => {
  const [earlier, later] = a.isAfter(b) ? [b, a] : [a, b];
  const { since, until } = serializeCustomRange(earlier, later);
  return Date.parse(until) - Date.parse(since) > MAX_RANGE_SPAN_MS;
};

/**
 * `disabledDate` for the console's RangePicker.
 *
 * Two refusals, for two different reasons:
 *
 * - Beyond today. The backend does not forbid a future window outright — with
 *   both bounds sent it only checks `since <= until` — but a future window is
 *   guaranteed to come back empty, and an empty panel under a confident caption
 *   reads as "nothing happened" rather than "you asked about next week". Today
 *   itself stays selectable; "the last N days ending today" is the common ask.
 * - Further than the span cap from the other end of the selection. `info.from`
 *   is the bound the operator already clicked, whichever field they clicked it
 *   in, so this refuses the over-wide half of the calendar in both directions.
 */
export const makeRangeDisabledDate =
  (now: () => Dayjs = dayjs) =>
  (current: Dayjs, info?: { from?: Dayjs }): boolean => {
    if (!current) return false;
    // The clock is read HERE, per call, not captured when the rule is built.
    // The picker asks this question once per cell each time it opens, so the
    // read is free, and a console left open past midnight offers the new today
    // instead of refusing it — which a date captured at mount would do, and
    // would keep doing until the operator reloaded. Injectable so a test can
    // pin "today" without touching the global clock.
    const today = now();
    if (current.startOf('day').isAfter(today.startOf('day'))) return true;
    const from = info?.from;
    return from ? isRangeSpanTooWide(from, current) : false;
  };

interface Ctx {
  rangeKey: RangeKey | 'custom';
  params: RangeParams;
  setRange: (k: RangeKey) => void;
  setCustom: (since: string, until: string) => void;
}

const AdminRangeContext = createContext<Ctx | null>(null);

/**
 * One value, not a key beside an optional pair. Two fields could hold
 * `rangeKey: 'custom'` with no bounds, which resolves to `{ range: 'custom' }`
 * — a range name the server does not have and answers with a 422. The setters
 * never produced it, but only an `as RangeKey` cast kept the compiler quiet
 * about it, which is the wrong thing to be relying on. Here it cannot be
 * written down.
 */
type Selection =
  | { kind: 'preset'; key: RangeKey }
  | { kind: 'custom'; since: string; until: string };

/**
 * Holds the console's one selected window, so every panel on a page asks the
 * same question.
 *
 * `params` is MEMOISED, and that is load-bearing rather than tidiness.
 * `PanelCard` compares its `deps` by value, which cannot help with a value that
 * is different on every render: an `active_since` computed from `Date.now()` at
 * render time would re-fetch every panel on the page on every keystroke into
 * the Activity page's search box. Presets sidestep the problem entirely by
 * sending the NAME of the window and letting the server resolve it; custom
 * bounds are fixed strings. Neither derives anything from the clock at render
 * time, and the memo keeps the object's identity stable too.
 */
export const AdminRangeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selection, setSelection] = useState<Selection>({ kind: 'preset', key: '30d' });

  const value = useMemo<Ctx>(
    () => ({
      rangeKey: selection.kind === 'preset' ? selection.key : 'custom',
      params:
        selection.kind === 'preset'
          ? { range: selection.key }
          : { since: selection.since, until: selection.until },
      // Both setters bail out when nothing actually changed — `useState` does
      // that for itself with an equal primitive, but not with the fresh object
      // this union is built from.
      //
      // What that is NOT for, measured rather than assumed: PanelCard. Without
      // the bail-out `params` really does churn (two objects for one re-pick),
      // and PanelCard still loads exactly once, because it compares `deps` by
      // JSON value — as the note on its `deps` prop says. What the bail-out
      // buys is the re-render itself: a new context value re-renders every
      // consumer on the page, so re-picking the window you are already on would
      // otherwise walk a grid of eight panels for no change. It also keeps
      // `params` safe to use as a hook dependency, which a consumer that is not
      // PanelCard would reasonably do.
      setRange: (k) =>
        setSelection((prev) => (prev.kind === 'preset' && prev.key === k ? prev : { kind: 'preset', key: k })),
      setCustom: (since, until) =>
        setSelection((prev) =>
          prev.kind === 'custom' && prev.since === since && prev.until === until
            ? prev
            : { kind: 'custom', since, until },
        ),
    }),
    [selection],
  );

  return <AdminRangeContext.Provider value={value}>{children}</AdminRangeContext.Provider>;
};

export const useAdminRange = (): Ctx => {
  const ctx = useContext(AdminRangeContext);
  if (!ctx) throw new Error('useAdminRange outside AdminRangeProvider');
  return ctx;
};

/** Human-readable statement of the selected window, for the page header. */
export const describeRange = (rangeKey: RangeKey | 'custom', params: RangeParams): string => {
  if (rangeKey === 'custom' && params.since && params.until) {
    const format = (iso: string) => dayjs(iso).format('MMM D, YYYY');
    return `${format(params.since)} – ${format(params.until)}`;
  }
  return `Last ${rangeKey.replace('d', '')} days`;
};
